import { v4 as uuidv4 } from "uuid";
import { SearchResultData } from "../../app/slices/SearchResultsSlice";
import { InteractionFactoryEntity } from "../../features/interaction_factory/InteractionFactory";
import ConnectQuizPreview from "../../features/previews/connect_quiz/ConnectQuizPreview";
import { GetMigratedProperties } from "../../features/project_migration/InteractionMigrationHelper";
import { ValidationHelper } from "../../features/validation/ValidationHelper";
import { Actor } from "../Actor";
import { requiredGlobalQuizProperties } from "../GlobalQuizProperty";
import {
  InteractionProperty,
  InteractionPropertyType,
} from "../InteractionProperty";
import { IAnswer } from "../Quiz";
import Take from "../Take";
import { IConnection, IConnectQuiz } from "./ConnectQuiz";
import Interaction, { InteractionType } from "./Interaction";
import { Prop } from "../Prop";
import { LocalizedStringRecord } from "../../app/slices/TrainingLocalizationSlice";
import { GetNewGuid } from "../../helpers/GuidHelper";

export const requiredConnectGraphicsProperties: InteractionProperty[] = [
  ...requiredGlobalQuizProperties,
  {
    id: "verticalLayout",
    name: "Vertical Layout",
    type: InteractionPropertyType.boolean,
    value: true,
  },
];

export const connectGraphicsQuizInteraction = (): InteractionFactoryEntity => {
  const component = <ConnectQuizPreview displayGraphics={true} />;

  const create = (getKeys: (amount: number) => string[]): IConnectQuiz => {
    const keys = getKeys(3);

    return {
      id: uuidv4(),
      name: "Connect Graphics Quiz",
      type: InteractionType.ConnectGraphicsQuiz,
      title: keys[0],
      answersA: [
        { id: uuidv4(), correct: false, text: keys[1], assetId: "" },
        { id: uuidv4(), correct: false, text: keys[2], assetId: "" },
      ],
      answersB: [
        { id: uuidv4(), correct: false, text: " ", assetId: "" },
        { id: uuidv4(), correct: false, text: " ", assetId: "" },
      ],
      connections: [],
      properties: requiredConnectGraphicsProperties,
    };
  };

  const migrate = (
    from: number,
    to: number,
    interaction: Interaction,
  ): IConnectQuiz => {
    const connectData = interaction as IConnectQuiz; // need to cast

    if (interaction.properties === undefined)
      return { ...connectData, properties: requiredConnectGraphicsProperties };

    var newProperties: InteractionProperty[] = GetMigratedProperties(
      requiredConnectGraphicsProperties,
      interaction.properties,
    );
    console.log(
      "[ConnectGraphicsQuiz] migrated " +
        (newProperties.length - interaction.properties.length) +
        " properties",
    );

    return { ...connectData, properties: newProperties };
  };

  const copy = (
    interaction: Interaction,
    getKeys: (amount: number) => string[],
    replaceKey: (from: string, to: string) => void,
    getAnswerArray: (
      originalAnswers: IAnswer[],
      newKeys: string[],
    ) => IAnswer[],
  ): IConnectQuiz => {
    const formQuiz = interaction as IConnectQuiz;

    const newKeys = getKeys(formQuiz.answersA.length + 1);

    replaceKey(formQuiz.title, newKeys[newKeys.length - 1]);

    const copiedAnswersA = getAnswerArray(
      formQuiz.answersA,
      newKeys.slice(0, formQuiz.answersA.length),
    );
    const copiedAnswersB: IAnswer[] = formQuiz.answersB.map((a) => ({
      ...a,
      id: GetNewGuid(),
      text: " ",
    }));
    const answerIdsA = new Map(
      formQuiz.answersA.map((answer, index) => [
        answer.id,
        copiedAnswersA[index].id,
      ]),
    );
    const answerIdsB = new Map(
      formQuiz.answersB.map((answer, index) => [
        answer.id,
        copiedAnswersB[index].id,
      ]),
    );
    const copiedConnections = formQuiz.connections.flatMap((connection) => {
      const idA = answerIdsA.get(connection.idA);
      const idB = answerIdsB.get(connection.idB);

      return idA === undefined || idB === undefined ? [] : [{ idA, idB }];
    });

    return {
      id: uuidv4(),
      name: interaction.name,
      type: InteractionType.ConnectGraphicsQuiz,
      title: newKeys[newKeys.length - 1],
      answersA: copiedAnswersA,
      answersB: copiedAnswersB,
      connections: copiedConnections,
      properties: formQuiz.properties?.map((property) => ({ ...property })),
    };
  };

  const validate = (
    interaction: Interaction,
    getValue: (key: string) => string,
    take: Take,
    actorPresets: Actor[],
  ): string[] => {
    const converted = interaction as IConnectQuiz;
    const warnings: string[] = [];

    ValidationHelper.ValidateKey(converted.title, getValue, warnings, "Title");

    // validate all answers A
    for (let i = 0; i < converted.answersA.length; i++) {
      const tempAnswer = converted.answersA[i];
      ValidationHelper.ValidateKey(
        tempAnswer.text,
        getValue,
        warnings,
        `Answer A ${i + 1}`,
      );
    }

    return warnings;
  };

  const filter = (
    interaction: Interaction,
    takeName: string,
    getMatchingResults: (
      searchableStrings: string[],
      takeName: string,
      interaction: Interaction,
      filterType: string,
    ) => SearchResultData[],
  ): SearchResultData[] => {
    const converted = interaction as IConnectQuiz;
    const searchableStrings: string[] = [
      converted.name,
      converted.title,
      ...converted.answersA.map((item) => item.text),
      ...converted.answersB.map((item) => item.text),
    ];

    return getMatchingResults(
      searchableStrings,
      takeName,
      interaction,
      filterType,
    );
  };

  const filterType: string = "ConnectGraphics Quiz";

  const validateErrors = (
    interaction: Interaction,
    parentTake: Take,
    currentTake: Take,
    actorPresets: Actor[],
    allPropPresets: Prop[],
    localizedStringRecords: LocalizedStringRecord,
  ): string[] => {
    const converted = interaction as IConnectQuiz;
    const errors: string[] = [];

    if (converted.connections.length === 0) {
      errors.push("Quiz must at least have one connection");
    }

    // validate all answers B
    for (let i = 0; i < converted.answersB.length; i++) {
      const tempAnswer = converted.answersB[i];
      ValidationHelper.ValidateAssetId(
        tempAnswer.assetId ?? "",
        errors,
        `Answer B ${i + 1} image`,
      );
    }

    return errors;
  };

  return {
    View: component,
    FilterType: filterType,
    Create: create,
    Migrate: migrate,
    Copy: copy,
    Validate: validate,
    Filter: filter,
    ValidateErrors: validateErrors,
  };
};
